import * as React from 'react';

import { isArrayWithItems } from '../../../utils';
import { RealtyRegions } from '../../../utils/regions_map';

import * as styles from './RoadsMap.css';
import { IHttpApi } from '@cian/http-api/shared';
import { ITelemetryLogger } from '@cian/telemetry/shared';
import { MapImage } from './MapImage';
import { IConfigUnsafe } from '@cian/config/shared';

export enum Regions {
  Moscow = 1,
  Spb,
  Rostov,
}

const SOURCES_MAP = {
  [Regions.Moscow]: {
    lat: 55.725047,
    lng: 37.646965,
  },
  [Regions.Spb]: {
    lat: 59.8,
    lng: 31.59,
  },
  [Regions.Rostov]: {
    lat: 47.222078,
    lng: 39.720349,
  },
};

const ROADS_MAP = {
  [Regions.Moscow]: 'MoscowRoads',
  [Regions.Spb]: 'SpbRoads',
  [Regions.Rostov]: 'RostovRoads',
};

export const getRegionById = (regionId: number) => {
  switch (regionId) {
    case RealtyRegions.Moscow:
    case RealtyRegions.MoscowArea:
    case RealtyRegions.MoscowAndArea:
      return Regions.Moscow;
    case RealtyRegions.StPetersburg:
    case RealtyRegions.StPetersburgArea:
    case RealtyRegions.StPetersburgAndArea:
      return Regions.Spb;
    case RealtyRegions.Rostov:
    case RealtyRegions.RostovArea:
      return Regions.Rostov;
    default:
      return null;
  }
};

export interface IRoadsMapProps {
  hovered: number[];
  regionId: number;
  selectedRoads: number[];
  httpApi: IHttpApi;
  telemetry: ITelemetryLogger;
  config: IConfigUnsafe;
}

export interface IRoadsMapState {
  error?: string;
  mapElement: React.LazyExoticComponent<React.ComponentType> | null;
}

export class RoadsMap extends React.Component<IRoadsMapProps, IRoadsMapState> {
  public state: IRoadsMapState = {
    error: '',
    mapElement: null,
  };

  private container = React.createRef<HTMLDivElement>();

  private hoveredRoad: SVGGElement[];

  public async componentDidMount() {
    const { regionId } = this.props;
    const region = getRegionById(regionId);

    if (region) {
      const mapElement = React.lazy(() => import(`./roads/${ROADS_MAP[region]}`));

      this.setState({ mapElement });
    }
  }

  public UNSAFE_componentWillReceiveProps(nextProps: IRoadsMapProps) {
    if (this.props.hovered !== nextProps.hovered || this.props.selectedRoads !== nextProps.selectedRoads) {
      this.changeHovered([...new Set([...nextProps.hovered, ...nextProps.selectedRoads])]);
    }
  }

  public render() {
    const mapImageSrc = this.getMapPreview();
    const { mapElement: MapElement } = this.state;

    if (!mapImageSrc || !MapElement) {
      return null;
    }

    return (
      <div ref={this.container} className={styles['container']}>
        <React.Suspense fallback="">
          <MapElement />
        </React.Suspense>
        <MapImage src={mapImageSrc} telemetry={this.props.telemetry} />
      </div>
    );
  }

  private getMapPreview = () => {
    const { config, regionId } = this.props;
    const region = getRegionById(regionId);
    const mapPreviewTemplate = config.get<string>('audience.mapPreviewTemplate');

    if (!region || !mapPreviewTemplate) {
      return null;
    }

    const coordinates: Record<string, number> = SOURCES_MAP[region];

    return mapPreviewTemplate.replace(/{{(lat|lng)}}/g, (_, matched: string) => `${coordinates[matched]}`);
  };

  private getSvgRoad = (id: number) => {
    return this.container.current && this.container.current.querySelector<SVGGElement>(`g[data-road-id="${id}"]`);
  };

  private setHovered = (hovered: boolean) => {
    if (!isArrayWithItems(this.hoveredRoad)) {
      return;
    }
    const svg = this.container.current && this.container.current.querySelector('svg');
    if (hovered && svg) {
      this.hoveredRoad.forEach(el => {
        // Используем setAttribute('class', '...'), т.к. classList в svg не работает в IE11
        el.setAttribute('class', styles['road_hovered']);
        // т.к. у SVG нет z-index, перемещаем элемент в самый конец SVG
        svg.appendChild(el);
      });
    } else {
      this.hoveredRoad.forEach(el => el.removeAttribute('class'));
    }
  };

  private changeHovered = (hovered?: number[]) => {
    const hoveredIds = hovered || this.props.selectedRoads;
    this.setHovered(false);
    this.hoveredRoad = hoveredIds.map(this.getSvgRoad).filter(Boolean) as SVGGElement[];
    this.setHovered(true);
  };
}
